// The OPORD (operation order): the scenario as a staff would hand it to you, built from the fitted
// scenario, the AO (area of operations) metadata and the dials. Nothing here invents facts the
// simulation does not hold — the one guess in the order, where the enemy's main effort is, is built
// from where red actually sits and is labelled as an S-2 (intelligence officer) assessment, because a
// planner must be able to tell what is known from what is estimated (spec §2, §7).

import template from '../../data/scenario-template.json';
import { UNIT_DEFS } from '../sim/scenario';
import { loadRules } from '../sim/rules';
import type { AoMeta, Dials, Scenario, UnitType } from '../sim/types';
import { aoOrientation } from '../map/draw';

// ---- terrain ---------------------------------------------------------------------------------------

export interface TerrainMix {
  /** Percentages of the AO, rounded, summing to 100. */
  open: number;
  restricted: number;
  nogo: number;
}

/**
 * The going, read straight off the movement-cost layer: cost 1-5 is open (a tracked vehicle keeps its
 * speed), 6 and up is restricted (slow going: forest, soft ground, steep slope), 0 is no-go (cliff,
 * open water, built-up core) and nothing crosses it.
 */
export function terrainMix(cost: Uint8Array): TerrainMix {
  let open = 0, restricted = 0, nogo = 0;
  for (let i = 0; i < cost.length; i++) {
    const c = cost[i];
    if (c === 0) nogo++;
    else if (c <= 5) open++;
    else restricted++;
  }
  const n = Math.max(1, cost.length);
  const openPct = Math.round((open / n) * 100);
  const restrictedPct = Math.round((restricted / n) * 100);
  return { open: openPct, restricted: restrictedPct, nogo: Math.max(0, 100 - openPct - restrictedPct) };
}

export function terrainLine(mix: TerrainMix): string {
  return `${mix.open}% open · ${mix.restricted}% restricted · ${mix.nogo}% no-go`;
}

/** The mission one-liner on an AO card: the same template task, in the language of that ground. */
export const AO_MISSION_LINE: Record<string, string> = {
  ntc: 'A task force attacks east down the desert corridor to seize an objective the enemy is defending in depth.',
  hohenfels: 'A task force attacks through close forest and hills to seize an objective the enemy is defending in depth.',
  suwalki: 'A task force attacks across open plains and lake defiles to seize an objective the enemy is defending in depth.',
  cheorwon: 'A task force attacks up a mountain valley to seize an objective the enemy is defending in depth.',
};

export function missionLineFor(meta: AoMeta): string {
  return (
    AO_MISSION_LINE[meta.name] ??
    'A task force attacks to seize an objective while the enemy defends in depth.'
  );
}

// ---- the order -------------------------------------------------------------------------------------

export interface OpordUnitEntry {
  /** The id the simulation will give this unit: its index in `scenario.blue`. */
  id: number;
  callsign: string;
  type: UnitType;
  label: string;
  sidc: string;
}

export interface OpordGroup { key: string; title: string; note: string; units: OpordUnitEntry[] }

export interface Opord {
  heading: string;
  aoTitle: string;
  situation: {
    enemy: string;
    /** What red is doing, in words, from the red-posture dial. */
    posture: string;
    /** The one estimate in the order. Always rendered under an "S-2 ASSESSMENT" chip. */
    assessment: string;
    terrain: string | null;
  };
  mission: string;
  intent: { purpose: string; keyTasks: string[]; endState: string };
  taskOrg: OpordGroup[];
}

const RED_POSTURE_WORDS: Record<Dials['red'], string> = {
  screen:
    'screens well forward with its reconnaissance on PL RED and holds the bulk of its combat power back, ready to concentrate once our direction of attack is clear',
  depth:
    'defends in depth: its main body is dug in on the high ground between PL WHITE and the objective, with an armored reserve staged behind it',
  forward:
    'defends well forward, massed on PL RED, and intends to stop the attack before it gains any depth at all',
};

/** Deterministic pick so the same seed always yields the same order text. */
function pick<T>(list: T[], seed: number, salt: number): T {
  return list[Math.abs((seed + salt * 2654435761) >>> 0) % list.length];
}

function fill(s: string, scenario: Scenario): string {
  return s
    .replaceAll('{tf}', scenario.names.tf)
    .replaceAll('{obj}', `OBJ ${scenario.names.obj}`)
    .replaceAll('{enemy}', scenario.names.enemy)
    .replaceAll('{hHour}', 'at H-hour');
}

/** Company letter from a callsign such as "A11" (Company A, 1st platoon); null for support callsigns. */
export function companyOf(callsign: string): string | null {
  return /^[A-Z]\d\d$/.test(callsign) ? callsign[0] : null;
}

/**
 * Where the S-2 thinks the enemy's weight is. Read off the centroid of red's actual positions across
 * the width of the corridor, then named as an avenue of approach, because that is the language a
 * planner thinks in — and it stays an estimate, not a fact, because blue has not seen red yet.
 */
function mainEffortAssessment(scenario: Scenario, meta: AoMeta): string {
  const orientation = aoOrientation(scenario);
  const widthCm = (orientation === 'WE' ? meta.h : meta.w) * meta.cellM * 100;
  let sum = 0;
  for (const p of scenario.red) sum += orientation === 'WE' ? p.y : p.x;
  const frac = scenario.red.length > 0 ? sum / scenario.red.length / Math.max(1, widthCm) : 0.5;
  const names = orientation === 'WE'
    ? ['southern', 'central', 'northern']
    : ['western', 'central', 'eastern'];
  const avenue = names[frac < 0.34 ? 0 : frac < 0.67 ? 1 : 2];
  const nai = scenario.graphics.filter((g) => g.kind === 'nai');
  const watch = nai[Math.min(2, nai.length - 1)]?.name ?? 'the chokepoints in zone';
  return `The enemy's main effort is assessed on the ${avenue} avenue of approach, with the decisive engagement area covering ${watch}. This is an estimate from terrain and pattern, not a confirmed report — treat it as a question to answer, not ground truth.`;
}

const SUPPORT_NOTE: Record<string, string> = {
  recon: 'Your eyes. They see first and report; they do not fight for information.',
  spArty: 'Self-propelled artillery. Call for fire before a company closes inside direct-fire range.',
  mortar: 'Task force mortars: fast, short-legged, good for smoke and suppression.',
  uas: 'Unmanned aircraft. They start on the ground — the Launch UAS card puts an airframe up, and electronic warfare shortens its life.',
  hq: 'The headquarters. It issues every order; lose it and the command net slows.',
  log: 'Logistics. Resupply and casualty evacuation both come from here.',
};

export function buildOpord(scenario: Scenario, meta: AoMeta, dials: Dials, cost?: Uint8Array): Opord {
  const seed = scenario.seed;
  // Morale parity by default (pitch 9a): red starts at 100 same as blue unless the active ruleset says
  // otherwise, and when it does, the OPORD discloses it here rather than leaving it a quiet asymmetry.
  const rules = loadRules(dials.rules.preset, dials.rules.overrides);
  const moraleNote = rules.redMoraleStart < 100 ? ` Enemy morale assessed at ${Math.round(rules.redMoraleStart)}.` : '';
  const missionPhrase: string = (template as { missionPhrase?: string }).missionPhrase
    ?? '{tf} attacks at H-hour to seize {obj} to enable brigade passage';
  const intent = (template as unknown as { intent: { purpose: string[]; keyTasks: string[]; endState: string[] } }).intent;

  // Task organization: maneuver companies by callsign letter, then everything that supports them.
  const byCompany = new Map<string, OpordUnitEntry[]>();
  const support: OpordUnitEntry[] = [];
  scenario.blue.forEach((p, id) => {
    const def = UNIT_DEFS[p.type];
    const entry: OpordUnitEntry = { id, callsign: p.callsign, type: p.type, label: def.label, sidc: def.sidcBlue };
    const company = companyOf(p.callsign);
    if (company === null) { support.push(entry); return; }
    const list = byCompany.get(company);
    if (list) list.push(entry);
    else byCompany.set(company, [entry]);
  });

  const taskOrg: OpordGroup[] = [...byCompany.entries()].map(([letter, units]) => ({
    key: letter,
    title: `Company ${letter}`,
    note: `${units.length} × ${UNIT_DEFS[units[0].type].label.toLowerCase()} platoon`,
    units,
  }));
  if (support.length > 0) {
    const kinds = [...new Set(support.map((u) => u.type))];
    taskOrg.push({
      key: 'tf',
      title: 'Task force troops',
      note: kinds.map((k) => SUPPORT_NOTE[k] ?? UNIT_DEFS[k].label).join(' '),
      units: support,
    });
  }

  return {
    heading: `OPORD 01 — ${scenario.names.tf}`,
    aoTitle: meta.title,
    situation: {
      enemy: `Opposite you is ${scenario.names.enemy}, roughly ${scenario.red.length} maneuver and supporting elements.${moraleNote}`,
      posture: `It ${RED_POSTURE_WORDS[dials.red]}.`,
      assessment: mainEffortAssessment(scenario, meta),
      terrain: cost ? `Going across the area of operations: ${terrainLine(terrainMix(cost))}.` : null,
    },
    mission: `${fill(missionPhrase, scenario)}.`,
    intent: {
      purpose: fill(pick(intent.purpose, seed, 1), scenario),
      keyTasks: intent.keyTasks.map((t) => fill(t, scenario)),
      endState: fill(pick(intent.endState, seed, 2), scenario),
    },
    taskOrg,
  };
}
